// 합충 · 신살: how the hour, month and year pillars meet the day pillar, and each pillar's
// 신살, in the app's table (RelationsTable.tsx). It draws the same natal calculation as 명식.
import { OHAENG_COLORS, SUBJECT_KEYS, componentError, requestSaju, sameSubject, validateCalculation, validateSubject } from './component-saju.mjs';
import { NATAL_SOURCE, normalizePaljaResponse, validateNatalData } from './component-saju-palja.mjs';

const object = value => value !== null && typeof value === 'object' && !Array.isArray(value);
const PILLARS = Object.freeze([['hour', '시주'], ['day', '일주'], ['month', '월주'], ['year', '년주']]);
const ROWS = Object.freeze([['stem', '천간 합충'], ['branch', '지지 합충'], ['shenshal', '신살']]);

export function validateRelationsProps(props) {
  if (!object(props) || Object.keys(props).some(key => ![...SUBJECT_KEYS, 'title', 'accent'].includes(key))) throw componentError('INVALID_COMPONENT', '합충 설정 필드를 확인해 주세요.');
  validateSubject(props);
  if (typeof props.title !== 'string' || !props.title.trim() || props.title.length > 100 || !/^#[0-9a-f]{6}$/i.test(props.accent)) throw componentError('INVALID_COMPONENT', '제목과 강조색을 확인해 주세요.');
  return props;
}

export async function calculateRelations(props, options = {}) {
  validateRelationsProps(props);
  // The natal route answers with /api/calculate's pillars and relations, the data of 명식 as well.
  const data = normalizePaljaResponse(await requestSaju('palja', props, options));
  return { source: NATAL_SOURCE, calculatedAt: new Date().toISOString(), data };
}

function cellText(row, key, item, relations) {
  if (item === null) return '시각 모름';
  if (row === 'shenshal') return item.shenshal.length ? item.shenshal.join('\n') : '—';
  if (key === 'day') return '기준';
  const list = relations[row === 'stem' ? 'stems' : 'branches'][key];
  return list.length ? list.join('\n') : '—';
}

export function renderRelationsScene(props, snapshot) {
  validateRelationsProps(props);
  const { data } = snapshot, elements = [];
  const rect = (key, x, y, width, height, fill, extra = {}) => elements.push({ key, type: 'rect', x, y, width, height, fill, stroke: '#d8e0dc', strokeWidth: 1, ...extra });
  const write = (key, x, y, width, height, value, fontSize, color = '#24352f', fontFamily = 'Content KoPub Dotum') => elements.push({ key, type: 'text', x, y, width, height, text: value, fontSize, fontFamily, color });
  rect('background', 0, 0, 1000, 540, '#f6f8f6', { stroke: 'transparent', strokeWidth: 0 });
  write('title', 32, 22, 936, 44, props.title, 30);
  write('subtitle', 32, 70, 936, 24, `일주를 기준으로 시주·월주·년주의 관계와 기둥별 신살 · 양력 ${data.birth.solar}${data.birth.unknownTime ? ' · 태어난 시각 모름' : ''}`, 15, '#52665d');
  const label = 140, column = 205, left = 30, top = 110, header = 56, heights = [96, 96, 136];
  rect('header-label-box', left, top, label, header, '#eef2ef');
  write('header-label', left + 16, top + 16, label - 24, 24, '구분', 16, '#52665d');
  PILLARS.forEach(([key, name], slot) => {
    const x = left + label + slot * column, item = data.pillars[slot];
    // The day pillar is the one the others are read against, as the app highlights it.
    rect(`header-box-${key}`, x, top, column, header, key === 'day' ? '#fff4e0' : '#eef2ef');
    write(`header-${key}`, x + 14, top + 8, column - 24, 20, name, 15, key === 'day' ? props.accent : '#52665d');
    const color = item === null ? '#52665d' : OHAENG_COLORS[item.stemOhaeng].text;
    write(`header-ganji-${key}`, x + 14, top + 28, column - 24, 24, item === null ? '?' : `${item.stemHanja}${item.branchHanja} ${item.stem}${item.branch}`, 17, color, 'Content KoPub Batang');
  });
  let y = top + header;
  ROWS.forEach(([row, name], index) => {
    const height = heights[index];
    rect(`row-label-box-${row}`, left, y, label, height, '#f9faf9');
    write(`row-label-${row}`, left + 16, y + 14, label - 24, height - 20, name, 16, '#24352f');
    PILLARS.forEach(([key], slot) => {
      const x = left + label + slot * column;
      rect(`cell-box-${row}-${key}`, x, y, column, height, key === 'day' && row !== 'shenshal' ? '#fffaf0' : '#ffffff');
      write(`cell-${row}-${key}`, x + 14, y + 12, column - 24, height - 20, cellText(row, key, data.pillars[slot], data.relations), 15, key === 'day' && row !== 'shenshal' ? '#8a6d3b' : '#24352f');
    });
    y += height;
  });
  write('calculated-at', 32, 506, 936, 20, `계산: ${snapshot.calculatedAt.slice(0, 10)} · 사주 앱 명식 계산 결과`, 12, '#52665d');
  return { width: 1000, height: 540, elements };
}

export const RELATIONS_COMPONENT = Object.freeze({
  type: 'saju.relations', version: 1, label: '합충 · 신살',
  description: '생년월일로 계산한 기둥끼리의 합충과 기둥별 신살을 일주 기준 표로 현재 페이지에 추가합니다.',
  fields: Object.freeze([
    Object.freeze({ name: 'title', label: '제목', kind: 'text', default: '합충과 신살의 자리' }),
    Object.freeze({ name: 'accent', label: '강조색', kind: 'color', default: '#b7791f' }),
  ]),
  validateProps: validateRelationsProps,
  validateSnapshot: snapshot => validateCalculation(snapshot, NATAL_SOURCE, validateNatalData),
  calculate: calculateRelations, render: renderRelationsScene,
  calculationChanged: (before, after) => !sameSubject(before, after),
});
